import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as CalendarDots } from '../../../../assets/icons/v3/calendar-dots.svg';
import { ReactComponent as Files } from '../../../../assets/icons/v3/files.svg';
import StyledTable from '../../../../shared/components/v3/Table/StyledTable/StyledTable';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { REPORTS_MONTHLY_TABLE_COLUMNS } from '../../../../shared/handlers/tableColumns';
import { IReport } from '../../../../shared/interfaces/reports';
import Company from '../../../../shared/services/api/companies/companies';
import { TRANSLATIONS_REPORTS } from '../../../../shared/translations/reports';
import { sortArray } from '../../../../shared/utils/utils';

import '../ReportsTable.scss';

export const MonthlyReportsTable = () => {
  const [reports, setReports] = useState<IReport[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { companyData, isLoadingCompanyData, isSpain } = useContext(CompanyContext);

  const { t } = useTranslation();

  const contentTitle = useMemo(
    () => (isSpain ? TRANSLATIONS_REPORTS.rf.monthly.title : TRANSLATIONS_REPORTS.default.title),
    [isSpain]
  );
  const contentDescription = useMemo(
    () => (isSpain ? TRANSLATIONS_REPORTS.rf.monthly.description : TRANSLATIONS_REPORTS.default.description),
    [isSpain]
  );

  const isValidCompany = useCallback(() => {
    return companyData?.id !== '' && companyData?.locale;
  }, [companyData]);

  const fetchMonthlyReports = useCallback(async () => {
    setLoading(true);
    try {
      const companyReports = await Company.getMonthlyReports(companyData.id);
      if (companyReports) {
        setReports(companyReports.reports);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [companyData.id]);

  const sortTableData = (columnKey: string, isAscending: boolean) => {
    let sortedData: IReport[] = [];
    if (columnKey === 'generationDate') {
      sortedData = sortArray([...reports], 'period', isAscending) as IReport[];
    } else {
      sortedData = sortArray([...reports], columnKey, isAscending) as IReport[];
    }
    setReports(sortedData);
  };

  useEffect(() => {
    if (isValidCompany() && !isLoadingCompanyData) {
      fetchMonthlyReports();
    }
  }, [companyData, isLoadingCompanyData, isValidCompany, fetchMonthlyReports]);

  return (
    <div className="reports-tabs-container">
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="reports-title">{t(contentTitle)}</div>
        <div className="reports-info flex flex-row">
          <div className="reports-frecuency flex flex-row items-center gap-3">
            <CalendarDots className="calendar-dots" />
            <span className="reports-frecuency-label">
              <Trans i18nKey={TRANSLATIONS_REPORTS.frequency.monthly} components={{ b: <b></b> }} />
            </span>
          </div>
          <div className="reports-format flex flex-row items-center gap-3">
            <Files />
            <span className="reports-format-label">
              <Trans i18nKey={TRANSLATIONS_REPORTS.formatPdf} components={{ b: <b></b> }} />
            </span>
          </div>
        </div>
      </div>
      <div className="reports-description mb-16">{t(contentDescription)}</div>
      <StyledTable
        data={reports}
        columns={REPORTS_MONTHLY_TABLE_COLUMNS}
        sortData={sortTableData}
        error={error}
        errorAction={fetchMonthlyReports}
        loading={loading || isLoadingCompanyData}
        emptyCard={{
          primaryText: TRANSLATIONS_REPORTS.table.emptyState.title,
          secondaryText: TRANSLATIONS_REPORTS.table.emptyState.description
        }}
      />
    </div>
  );
};

export default MonthlyReportsTable;
